import React, { createContext, useContext, useMemo } from 'react';

import moment from 'moment';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';

import {
  sortBooleanWithNullOrUndefinedValues,
  sortWithNullOrUndefinedValues,
} from '../internals/sort';

import { PipeLastRunStatusContainer } from '../components/pipe-status/PipeLastRunStatus.container';

import Tooltip from '@material-ui/core/Tooltip';
import SesamLink from '../components/common/Links/SesamLink';
import Ellipsis from '../components/common/Ellipsis/index';
import PipeLink from '../components/common/Links/PipeLink';
import InfoTooltip from '../components/common/InfoTooltip/InfoTooltip';
import PipeStateRenderer from '../components/common/table-list-renderers/pipe-state';
import State from '../components/common/renderers/State/State';
import PipeProgressRenderer from '../components/common/table-list-renderers/pipe-progress';
import PipeEntitiesPerSecondRenderer from '../components/common/table-list-renderers/pipe-entities-per-second';
import { PipeNextRunStatus } from '../components/pipe-status/PipeNextRunStatus';
import { getSortValues } from '../components/filter-table/FilterTable';
import ConfigStateRenderer from '../components/common/table-list-renderers/config-state';
import { SubscriptionContext } from '../pages/Subscription.page';

import { getFromToObjectNameFn } from '../utils/pipes.utils';
import { getEntitiesPerSecond } from '../internals/datasets';
import { bytesToSize, withSeparators } from '../internals/utils';
import { getPipeQueueSize, getSinkDataset, pipeConfigSorter } from '../internals/pipes';

import type { SomeObject } from '../types/common.types';
import type { DatasetMap } from '../types/dataset.types';
import type { SystemMap } from '../types/system.types';
import type { Pipe, PipeConfigSource } from '../types/pipes.types';
import type { FilterCell } from '../types/pipesList.types';

const getFromObjectName = getFromToObjectNameFn('from');
const getToObjectName = getFromToObjectNameFn('to');

export function BytesUsedCell({ cell: { value } }: FilterCell) {
  const { showBytesExact, toggleShowBytesExact } = useContext(PipesListContext);

  if (isUndefined(value)) {
    return '-';
  }

  return (
    <span
      onClick={() => toggleShowBytesExact?.()}
      title="Click to switch between representation"
      style={{ cursor: 'pointer' }}
    >
      {showBytesExact ? withSeparators(value) : bytesToSize(value)}
    </span>
  );
}

type LeafPipe = string;
type LeafPipes = LeafPipe[];

export const PipesListContext = createContext<{
  datasets: DatasetMap;
  systems: SystemMap;
  showBytesExact?: boolean;
  toggleShowBytesExact?: () => void;
}>({
  datasets: {},
  systems: {},
});

const getFromName = (source: PipeConfigSource, systems: SystemMap) => {
  if (source.system) {
    return getFromObjectName(source, systems[source.system]);
  }
};

const getToName = (sink: PipeConfigSource, systems: SystemMap) => {
  if (sink.system) {
    return getToObjectName(sink, systems[sink.system]);
  }
};

const PipeFromCell = ({ cell: { value: sink } }: FilterCell) => {
  const { systems } = useContext(PipesListContext);

  return getFromName(sink, systems) || '';
};

const PipeToCell = ({ cell: { value: sink } }: FilterCell) => {
  const { systems } = useContext(PipesListContext);

  return getToName(sink, systems) || '';
};

export const usePipesListColumns = (
  leafPipes: LeafPipes,
  datasets: DatasetMap,
  systems: SystemMap
) => {
  const columns = useMemo(() => {
    return [
      {
        Header: 'Pipe',
        id: 'pipes-link',
        fixed: true,
        width: 300,
        accessor: (d) => ({
          id: d._id,
          link: d.link,
          description: get(d, ['config', 'original', 'description']),
        }),
        Cell: ({ cell: { value } }: FilterCell) => (
          <div style={{ display: 'flex' }}>
            <Ellipsis>
              <PipeLink pipeId={value.link}>{value.id}</PipeLink>
            </Ellipsis>
            {value.description && (
              <InfoTooltip info={value.description} style={{ marginLeft: '5px' }} />
            )}
          </div>
        ),
        sortType: getSortValues((a: SomeObject, b: SomeObject) =>
          sortWithNullOrUndefinedValues(a.id, b.id)
        ),
      },
      {
        Header: 'Name',
        id: 'pipes-name',
        accessor: (d) => ({ name: d.name, link: d.link }),
        Cell: ({ cell: { value } }: FilterCell) => (
          <PipeLink pipeId={value.link}>{value.name}</PipeLink>
        ),
        sortType: getSortValues((a: SomeObject, b: SomeObject) =>
          sortWithNullOrUndefinedValues(a.name, b.name)
        ),
      },
      {
        Header: 'From',
        id: 'pipes-from',
        accessor: 'config.effective.source',
        Cell: PipeFromCell,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'To',
        id: 'pipes-to',
        accessor: 'config.effective.sink',
        Cell: PipeToCell,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Transforms',
        id: 'transforms',
        accessor: 'transforms',
        Cell: ({ cell: { value } }: FilterCell) => <span>{value}</span>,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Enabled',
        id: 'pipes-enabled',
        accessor: 'runtime[is-disabled]',
        align: 'center',
        width: 75,
        Cell: ({ cell: { value } }: FilterCell) =>
          !value ? (
            <State title="Pipe is enabled" className="status-green" text="Yes" />
          ) : (
            <State title="Pipe is disabled" className="status-red" text="No" />
          ),
        sortType: getSortValues((a: SomeObject, b: SomeObject) =>
          sortBooleanWithNullOrUndefinedValues(!a, !b)
        ),
      },
      {
        Header: 'State',
        id: 'pipes-state',
        accessor: 'runtime.state',
        Cell: ({ cell: { value } }: FilterCell) => <PipeStateRenderer state={value} />,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Global',
        id: 'pipes-global',
        accessor: 'global',
        align: 'center',
        width: 75,
        Cell: ({ cell: { value } }: FilterCell) =>
          value === 'Yes' ? (
            <State title="Pipe is a global" className="status-green" text="Yes" />
          ) : (
            <State title="Pipe is not a global" className="status-red" text="No" />
          ),
        sortType: getSortValues((a: string, b: string) => {
          const aBool = a === 'Yes';
          const bBool = b === 'Yes';
          return sortBooleanWithNullOrUndefinedValues(aBool, bBool);
        }),
      },
      {
        Header: 'Progress',
        id: 'pipes-progress',
        accessor: (d) => ({
          runtime: d.runtime,
          progress: get(d, 'runtime.progress'),
        }),
        Cell: ({ cell: { value } }: FilterCell) => (
          <PipeProgressRenderer runtime={value.runtime} progress={value.progress} />
        ),
        sortType: getSortValues((a: SomeObject, b: SomeObject) => {
          const aLastRun = get(a, 'progress["last-run"]');
          const bLastRun = get(b, 'progress["last-run"]');
          return sortWithNullOrUndefinedValues(aLastRun, bLastRun);
        }),
      },
      {
        Header: 'Entities/second',
        id: 'pipes-entities-per-second',
        accessor: 'runtime',
        Cell: ({ cell: { value } }: FilterCell) => (
          <PipeEntitiesPerSecondRenderer runtime={value} />
        ),
        sortType: getSortValues((a: SomeObject, b: SomeObject) => {
          const aEps = getEntitiesPerSecond(a);
          const bEps = getEntitiesPerSecond(b);

          return sortWithNullOrUndefinedValues(aEps, bEps);
        }),
      },
      {
        Header: 'Next run',
        id: 'pipes-next-run',
        accessor: 'runtime[next-run]',
        Cell: ({ cell: { value, row } }) => (
          <PipeNextRunStatus nextRun={value} type={get(row, 'original.config.effective.type')} />
        ),
        sortType: getSortValues((a: SomeObject, b: SomeObject) => {
          return sortWithNullOrUndefinedValues(new Date(a), new Date(b));
        }),
      },
      {
        Header: 'Last run',
        id: 'pipes-last-run',
        accessor: 'runtime',
        Cell: ({ cell: { value, row } }) => {
          const subId = useContext(SubscriptionContext);
          return (
            <PipeLastRunStatusContainer
              subId={subId}
              runtime={value}
              showMore={false}
              id={get(row, 'original.config.effective._id')}
              type={get(row, 'original.config.effective.type')}
            />
          );
        },
        sortType: getSortValues((a: SomeObject, b: SomeObject) => {
          const aLastRun = a['last-run'];
          const bLastRun = b['last-run'];
          return sortWithNullOrUndefinedValues(new Date(aLastRun), new Date(bLastRun));
        }),
      },
      {
        Header: 'Last seen',
        id: 'pipes-last-seen',
        accessor: 'runtime[last-seen]',
        Cell: ({ cell: { value } }: FilterCell) => <div>{isNil(value) ? '' : value}</div>,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Bytes used',
        id: 'pipes-bytes-used',
        accessor: (d) => (isObject(d.storage) ? d.storage.total : d.storage),
        align: 'right',
        Cell: BytesUsedCell,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Queues',
        id: 'pipes-queues',
        accessor: (p) => p,
        Cell: ({ cell: { value } }: FilterCell) => (
          <div>{withSeparators(getPipeQueueSize(value))}</div>
        ),
        sortType: getSortValues((a: Pipe, b: Pipe) => {
          const aQ = getPipeQueueSize(a);
          const bQ = getPipeQueueSize(b);

          return sortWithNullOrUndefinedValues(aQ, bQ);
        }),
      },
      {
        Header: 'Origin',
        id: 'pipes-origin',
        accessor: 'runtime.origin',
        Cell: ({ cell: { value } }: FilterCell) => <span>{value}</span>,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Config',
        id: 'pipes-config',
        accessor: (p) => p,
        align: 'center',
        Cell: ({ cell: { value } }: FilterCell) => (
          <ConfigStateRenderer value={value} type="pipe" />
        ),
        sortType: getSortValues(pipeConfigSorter),
      },
      {
        Header: 'Config group',
        id: 'pipes-config-group',
        accessor: 'configGroup',
        Cell: ({ cell: { value } }: FilterCell) => {
          return <span>{value}</span>;
        },
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Pump mode',
        id: 'pipes-mode',
        accessor: 'pumpMode',
        style: { textAlign: 'center' },
      },
      {
        Header: 'Populated',
        id: 'pipes-populated',
        align: 'center',
        accessor: (p) => getSinkDataset(p, datasets),
        width: 75,
        Cell: ({ cell: { value: sinkDataset } }) => {
          if (sinkDataset) {
            return get(sinkDataset, 'runtime.populated') ? (
              <State title="Pipe's sink dataset is populated" className="status-green" text="Yes" />
            ) : (
              <State
                title="Pipes's sink dataset is not populated"
                className="status-red"
                text="No"
              />
            );
          } else {
            return '-';
          }
        },
        sortType: getSortValues((a: SomeObject, b: SomeObject) => {
          const aSinkPopulated = get(a, 'runtime.populated');
          const bSinkPopulated = get(b, 'runtime.populated');

          return sortBooleanWithNullOrUndefinedValues(aSinkPopulated, bSinkPopulated);
        }),
      },
      {
        Header: 'Index w/o deleted',
        id: 'pipes-index-without-deleted',
        accessor: (p) => getSinkDataset(p, datasets),
        Cell: ({ cell: { value: sinkDataset } }) => {
          if (sinkDataset) {
            const count =
              sinkDataset.runtime['count-index-exists'] -
              sinkDataset.runtime['count-index-deleted'];
            return <span>{withSeparators(count)}</span>;
          } else {
            return '-';
          }
        },
        sortType: getSortValues((a: SomeObject, b: SomeObject) => {
          let aCount = a
            ? a.runtime['count-index-exists'] - a.runtime['count-index-deleted']
            : null;
          let bCount = b
            ? b.runtime['count-index-exists'] - b.runtime['count-index-deleted']
            : null;

          return sortWithNullOrUndefinedValues(aCount, bCount);
        }),
      },
      {
        Header: 'Complete index',
        id: 'pipes-complete-index',
        accessor: (p) => getSinkDataset(p, datasets),
        Cell: ({ cell: { value: sinkDataset } }) => {
          if (sinkDataset) {
            const count = sinkDataset.runtime['count-index-exists'];
            return <span>{withSeparators(count)}</span>;
          } else {
            return '-';
          }
        },
        sortType: getSortValues((a: SomeObject, b: SomeObject) => {
          let aCount = a ? a.runtime['count-index-exists'] : null;
          let bCount = b ? b.runtime['count-index-exists'] : null;

          return sortWithNullOrUndefinedValues(aCount, bCount);
        }),
      },
      {
        Header: 'Complete log',
        id: 'pipes-complete-log',
        accessor: (p) => getSinkDataset(p, datasets),
        Cell: ({ cell: { value: sinkDataset } }) => {
          if (sinkDataset) {
            const count = sinkDataset.runtime['count-log-exists'];
            return <span>{withSeparators(count)}</span>;
          } else {
            return '-';
          }
        },
        sortType: getSortValues((a: SomeObject, b: SomeObject) => {
          let aCount = a ? a.runtime['count-log-exists'] : null;
          let bCount = b ? b.runtime['count-log-exists'] : null;
          return sortWithNullOrUndefinedValues(aCount, bCount);
        }),
      },
      {
        Header: 'Leaf',
        id: 'pipes-leaf',
        align: 'center',
        accessor: (p) => p._id,
        width: 95,
        Cell: ({ cell: { value: pipeId } }) => {
          if (pipeId) {
            return leafPipes.includes(pipeId) ? (
              <State title="This pipe has no downstream pipes" className="status-red" text="Yes" />
            ) : (
              <State title="This pipe has downstream pipes" className="status-green" text="No" />
            );
          }
        },
        sortType: getSortValues((a: SomeObject, b: SomeObject) => {
          const aLeaf = leafPipes.includes(a);
          const bLeaf = leafPipes.includes(b);

          return sortBooleanWithNullOrUndefinedValues(aLeaf, bLeaf);
        }),
      },
      {
        Header: 'Completeness',
        id: 'pipes-completeness',
        accessor: (p) => ({
          sinkDataset: getSinkDataset(p, datasets),
          pipeId: p._id,
        }),
        Cell: ({ cell: { value } }: FilterCell) => {
          const subId = useContext(SubscriptionContext);
          if (value.sinkDataset) {
            const completeness = get(value.sinkDataset, ['runtime', 'completeness']);

            if (typeof completeness === 'number') {
              const completenessRelative = moment(completeness / 1000).fromNow();
              const completenessTotal = moment(completeness / 1000).format(
                'do MMM YYYY - HH:mm:ss'
              );
              return (
                <Tooltip title={completenessTotal}>
                  <SesamLink
                    to={`/subscription/${subId}/pipes/pipe/${
                      value.pipeId
                    }/output?jtype=updated&jval=${completeness / 1000}`}
                  >
                    {completenessRelative}
                  </SesamLink>
                </Tooltip>
              );
            }
          }
          return '-';
        },
        sortType: getSortValues((a: SomeObject, b: SomeObject) => {
          let aCount = a.sinkDataset ? a.sinkDataset.runtime['completeness'] : null;
          let bCount = b.sinkDataset ? b.sinkDataset.runtime['completeness'] : null;
          return sortWithNullOrUndefinedValues(aCount, bCount);
        }),
      },
      {
        Header: 'Durable',
        id: 'durable-pipe',
        accessor: 'durable',
        align: 'center',
        width: 75,
        Cell: ({ cell: { value } }: FilterCell) =>
          value === 'Yes' ? (
            <State title="Pipe is durable" className="status-green" text="Yes" />
          ) : (
            <State title="Pipe is not durable" className="status-red" text="No" />
          ),
        sortType: getSortValues((a: string, b: string) => {
          const aBool = a === 'Yes';
          const bBool = b === 'Yes';
          return sortBooleanWithNullOrUndefinedValues(aBool, bBool);
        }),
      },
      {
        Header: 'Tenant',
        id: 'tenant-pipe',
        accessor: 'tenant',
        align: 'center',
        width: 75,
        Cell: ({ cell: { value } }: FilterCell) =>
          value !== 'Not Tenant' ? (
            <State title="Tenant pipe" className="status-green" text={value} />
          ) : (
            <State title="Not Tenant pipe" className="status-red" text="Not Tenant" />
          ),
        sortType: getSortValues((a: string, b: string) => {
          const aBool = a !== 'Not Tenant';
          const bBool = b !== 'Not Tenant';
          return sortBooleanWithNullOrUndefinedValues(aBool, bBool);
        }),
      },
    ];
  }, [sortBooleanWithNullOrUndefinedValues, datasets, systems, leafPipes]);

  return columns;
};
